import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IProfile } from "./types";
import { useSnackbar } from "../../contexts/SnackbarProvider";
import { FormikProvider, useFormik } from "formik";
import useUpdateData from "../../hooks/useUpdateData";
import {
  THEME_BLUE,
  Themes,
  TTheme,
  useThemesContext,
} from "../../contexts/ThemesProvider";
import DialogButtons from "../../Components/DialogButtons";
import FormikSelect, {
  TOption,
  TOptionType,
} from "../../Components/FormikSelect";
import {
  DefaultLanguage,
  TLanguage,
  useLanguage,
} from "../../contexts/LanguageProvider";
import { useTranslation } from "react-i18next";

const languageOptions: TOption<TOptionType>[] = [
  { label: "Deutsch", value: "de" },
  { label: "English", value: "en" },
];

interface IProfileFormProps {
  profile: IProfile;
}

const ProfileForm = (props: IProfileFormProps) => {
  const { profile } = props;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const { switchTheme } = useThemesContext();
  const { switchLanguage } = useLanguage();
  const { t } = useTranslation();

  const themeOptions: TOption<TOptionType>[] = Themes.reduce(
    (options: TOption<TOptionType>[], value) => {
      const colorName = value.split("_")[1];
      const label = colorName.charAt(0).toUpperCase() + colorName.slice(1);
      options.push({ label: t(label), value: value });
      return options;
    },
    []
  );

  const handleCancel = async () => {
    switchTheme(profile.theme || THEME_BLUE);
    showMessage({
      text: `Editing user "${profile.username}" cancelled`,
      severity: "info",
    });
    navigate("/");
  };

  const updateHandler = useUpdateData();
  const formik = useFormik({
    initialValues: {
      theme: profile.theme || THEME_BLUE,
      language: profile.language || DefaultLanguage,
    },
    onSubmit: async (values) => {
      setIsSubmitting(true);
      switchTheme(values.theme);
      updateHandler(
        `user "${profile.username}"`,
        `/users/${profile.id}/profile`,
        values
      );
      navigate("/");
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <FormikSelect
          fullWidth
          label={t("Theme")}
          name="theme"
          options={themeOptions}
          onChange={(e) => {
            formik.handleChange(e);
            switchTheme(e.target.value as TTheme);
          }}
          data-cy="profile-select-theme"
        />
        <FormikSelect
          fullWidth
          label="Language"
          name="language"
          options={languageOptions}
          onChange={(e) => {
            formik.handleChange(e);
            switchLanguage(e.target.value as TLanguage);
          }}
          data-cy="profile-select-theme"
        />
        <DialogButtons
          isSubmitting={isSubmitting}
          submitButtonLabel="Save"
          resetCallback={handleCancel}
        />
      </FormikProvider>
    </form>
  );
};

export default ProfileForm;
